import { useEffect, useMemo, useState, useRef } from 'react';
import { useActivityStore } from '@/store/activity';
import useStore from '@/store';
import {
  ActivityType,
  TaskStatus,
  SubTaskType,
  rewardInfo,
  subTaskListItem,
  taskDetail,
  BlinkData,
} from '@/types';
import { useFm, useWallet, useParams, useToast } from '@web3/base-hooks';
import { WalletChainTypes, WalletType, appPath } from '@web3/base-utils';
import { pageViewTrack } from '@web3/base-tracking';
import { isMobile, safeParseJSON } from '@web3/base-utils';
import { BybitWalletEx } from '@/config/common';
import { Page } from '@/config/track-config';
import { useUserInfo } from '@/hooks/user';
import { useBybitWallet } from '@/hooks/wallet';
import { PrepareTaskTypes, RewardFormType } from '@/types';
import { permissionStore } from '@web3fe/infra';
import { ACTIVITY_CHAIN_INFO } from '@/utils/constant';
import { bybitWalletAddChain } from '@/utils/appflyers';

interface CommonQuery {
  activityId: number;
  chainId: number;
  excludeTaskType: PrepareTaskTypes[];
  walletType: WalletType;
  walletId: string;
  walletAddress: string;
}

export const useMediaTask = (query: CommonQuery) => {
  const [showConnectX, setShowConnectX] = useState(false);
  const [showBlink, setShowBlink] = useState(false);
  const [blinkData, setBlinkData] = useState<BlinkData>();
  const { walletId, evmWalletInfo, walletType, isLogin } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const [taskApplyObject, setTaskApplyObject] = useState<subTaskListItem>();
  const { toastSuccess } = useToast();
  const fm = useFm();

  const twitterConnectCodeKey = `twitter-connect-code-${walletId}`;

  const activityStore = useActivityStore();

  const chainId = useMemo(
    () => activityStore.activityDetail?.activityInfo.chainId,
    [activityStore.activityDetail]
  );

  const onChangeApplyObject = (task: subTaskListItem) => {
    setTaskApplyObject(task);
  };

  // 社媒blink任务处理
  const doBlinkTask = (ruleConfig: any) => {
    activityStore
      .fetchActions({ actionId: ruleConfig.actionId })
      .then((res) => {
        setBlinkData({
          blinksUrl: ruleConfig.blinksUrl,
          ...res,
        });
        setShowBlink(true);
      });
    pageViewTrack({
      page_name: 'airdrop_task_blink',
      blinksUrl: ruleConfig.blinksUrl,
      wallet_address: evmWalletInfo.address,
    });
  };

  // 领取社媒子任务, blink任务也属于社媒任务
  const doMediaTask = async (applySubtask: subTaskListItem) => {
    const ruleConfig = safeParseJSON(applySubtask.taskRuleConfig, {} as any);
    let socialMediaName = '';
    // 社媒 关注任务
    if (applySubtask.taskType === SubTaskType.media) {
      socialMediaName = ruleConfig.socialMediaName;
      // 临时方案：ruleConfig里现在只有twitter，所以直接就使用twitter来拼接，后面如果有别的，需要再根据不同媒体实现
      if (!socialMediaName) {
        throw new Error('socialMediaName in subTask ruleConfig is required');
      }
    }

    // TODO
    if (!applySubtask.mediaAuthorized) {
      const code = localStorage.getItem(twitterConnectCodeKey);
      // 前面如果用户链接了twitter将 code存到local storage当作用户已经连接过，因为现在后端没有用户绑定功能。
      if (code) {
        // 授权过twitter的情况下，直接就用这个code saveMedia，并打开 twitter账号
        switch (applySubtask.taskType) {
          // 社媒 关注任务
          case SubTaskType.media:
            activityStore
              .doSaveSocialMedia({
                accessToken: code,
                recordId: applySubtask?.recordId,
                socialMediaId: '',
                socialMediaName: '',
                socialMediaType: 1,
                taskId: applySubtask.configId,
                chainId,
                walletId,
                walletType,
                walletAddress: walletAddresses.find(
                  (item) => +item.chainId === +chainId
                )?.walletAddress,
              })
              .then(() => {
                window.open(applySubtask.webGoUrl);
                activityStore.fetchTaskList(query);
              });
            break;
          // 社媒blink任务
          case SubTaskType.blink:
            doBlinkTask(ruleConfig);
            break;
        }
      } else {
        setShowConnectX(true);
      }
    } else {
      // 如果是已经授权关注，再次点击的话，
      switch (applySubtask.taskType) {
        // 社媒 关注任务 就打开配置的web链接
        case SubTaskType.media:
          window.open(applySubtask.webGoUrl);
          break;
        // 社媒blink任务
        case SubTaskType.blink:
          doBlinkTask(ruleConfig);
          break;
      }
    }
  };

  const onChangeShowConnectX = (v: boolean) => {
    setShowConnectX(v);
  };

  const onChangeShowBlink = (v: boolean) => {
    setShowBlink(v);
  };

  const doCallbackXTask = async (code) => {
    if (!isLogin) {
      return;
    }
    const { configId, taskType } = taskApplyObject;

    if (!configId) return;
    // 推特授权回来逻辑
    localStorage.setItem(twitterConnectCodeKey, code);

    switch (taskType) {
      // 社媒blink任务，跳过
      case SubTaskType.blink:
        break;
      // 如果是社媒 关注 任务，需要立即提交社媒信息
      default:
        onChangeShowConnectX(false);
        toastSuccess(fm('connectSuccessfully'));
        break;
    }
  };

  const doCallbackBlinkTask = async () => {
    if (!isLogin) {
      return;
    }

    if (!taskApplyObject) return;

    if (!taskApplyObject?.mediaAuthorized) {
      // blink任务点击post
      const taskId = Number(taskApplyObject?.configId);
      const recordId = Number(taskApplyObject?.recordId);
      activityStore
        .doSaveSocialMedia({
          accessToken: localStorage.getItem(twitterConnectCodeKey) ?? '',
          recordId,
          socialMediaId: '',
          socialMediaName: '',
          socialMediaType: 1,
          taskId,
          chainId,
          walletId,
          walletType,
          walletAddress: walletAddresses.find(
            (item) => +item.chainId === +chainId
          )?.walletAddress,
        })
        .then(() => {
          activityStore.fetchTaskList({
            ...query,
          });
        });
    }

    onChangeShowBlink(false);
  };

  return {
    showConnectX,
    showBlink,
    blinkData,
    onChangeShowConnectX,
    onChangeShowBlink,
    onChangeApplyObject,
    doBlinkTask,
    doMediaTask,
    doCallbackXTask,
    doCallbackBlinkTask,
  };
};

export const useClickTask = () => {
  const activityStore = useActivityStore();
  const { walletId, walletType } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const chainId = useMemo(
    () => activityStore.activityDetail?.activityInfo.chainId,
    [activityStore.activityDetail]
  );

  const doClickTask = async (applySubtask: subTaskListItem) => {
    activityStore.doTaskReport({
      taskId: applySubtask.configId,
      chainId: chainId,
      walletId,
      walletType,
      walletAddress: walletAddresses.find(
        (item) =>
          +item.chainId ===
          (+activityStore.activityDetail?.activityInfo?.chainId || 1)
      )?.walletAddress,
    });
  };

  return {
    doClickTask,
  };
};

export const useApplyTask = (
  query: CommonQuery,
  doMediaTask: (applySubtask: subTaskListItem) => Promise<void>,
  doClickTask: (applySubtask: subTaskListItem) => Promise<void>,
  doBlinkTask: (ruleConfig: any) => void,
  onChangeInstallBW,
  onShowSwitchWallet,
  onChangeCurrentTask,
  onShowBanModal: () => void,
  setTaskApplyObject: (task: subTaskListItem) => void
) => {
  const [enterPageApply, setEnterPageApply] = useState(false);
  const { activityId } = useParams();
  const {
    isLogin,
    walletId,
    walletType,
    openLoginModal,
    evmWalletInfo,
    createWallet,
    switchChain,
  } = useWallet();
  const { walletAddresses, initThirdAddresses } = useBybitWallet();
  const activityChainInfo = useStore((state) => state.activityChainInfo);

  const { toastWarn } = useToast();
  const fm = useFm();

  const activityStore = useActivityStore();

  const chainId = useMemo(
    () => activityStore.activityDetail?.activityInfo.chainId,
    [activityStore.activityDetail]
  );

  const walletTypeList = useMemo(
    () => activityStore.activityDetail?.activityInfo?.walletTypeList || [],
    [activityStore.activityDetail]
  );

  // 活动支持云钱包且未创建钱包
  const showCreateCloud = () => {
    if (
      walletType === WalletType.CLOUD &&
      !evmWalletInfo.address &&
      walletTypeList.includes(WalletType.CLOUD)
    ) {
      createWallet();
      return true;
    }
    return false;
  };

  const handleActivityApply = async (
    configId: number | undefined,
    subTask: subTaskListItem
  ) => {
    // if (true) {
    //   onShowBanModal();
    //   return;
    // }
    if (isLogin) {
      if (showCreateCloud()) return;

      // 任务未到开始时间
      if (subTask.startTime > +new Date()) {
        toastWarn(fm('mpsToastUpcoming'));
        return;
      }

      activityStore
        .doAtivityApply({
          taskId: configId,
          chainId,
          walletId,
          walletType,
          walletAddress: walletAddresses.find(
            (item) =>
              +item.chainId ===
              +activityStore.activityDetail?.activityInfo.chainId
          )?.walletAddress,
        })
        .then(async (applyRes) => {
          if (!applyRes.success) return;

          // 领取任务成功后更新任务列表，然后再更新领取的子任务的状态
          const res = await activityStore.fetchTaskList(query);

          const applyTask =
            res?.result?.filter((item) => item.configId === configId) || []; // 已领取子任务对应的父任务

          const applySubtask = applyTask[0].subTaskList.filter(
            (sub) => sub.configId === subTask.configId
          )[0];

          setTaskApplyObject(applySubtask);
          if (
            Number(activityId) !== 50 ||
            (Number(activityId) === 50 && enterPageApply)
          ) {
            switch (applySubtask.taskType) {
              case SubTaskType.media:
              case SubTaskType.blink:
                doMediaTask(applySubtask);
                break;
              case SubTaskType.click:
                window.open(activityStore.taskButtonUrl(applySubtask));
                doClickTask(applySubtask);
                break;
              default:
                window.open(activityStore.taskButtonUrl(applySubtask));
            }
          }
        });
    } else {
      openLoginModal();
    }
  };

  const beginActivityApply = async (
    subTask: subTaskListItem,
    item: taskDetail
  ) => {
    if (
      item.configId === 2254 &&
      ['US'].includes(permissionStore.state.countryCode)
    ) {
      onShowBanModal();
      return;
    }

    if (isMobile) {
      // eslint-disable-next-line
      const link = `https://app.bybit.com/inapp?by_dp=bybitapp%3A%2F%2Fopen%2Froute%3FtargetUrl%3Dby-mini%253A%252F%252Fweb3_applet%252Fmps2_activity_detail%253FactivityId%253D${activityId}&by_web_link=${encodeURIComponent(window.top.location.href || '')}`;
      window.top?.open(link);
      return;
    }

    //dapp任务判断是否安装bw
    if (
      activityStore.activityDetail?.activityInfo?.activityType !==
        ActivityType.noStatus &&
      subTask.taskType === 103 &&
      !window.top?.bybitWallet
    ) {
      onChangeInstallBW(true);
      return;
    }

    if (
      !activityStore.activityDetail?.activityInfo.clientConfig?.pcConfig.support
    ) {
      toastWarn(fm('notSuppOnPc'));
      return;
    }

    if (!walletTypeList.includes(walletType) && isLogin) {
      onShowSwitchWallet();
      return;
    }

    if (
      walletType === WalletType.MNEMONIC_PHRASE ||
      walletType === WalletType.THIRD_PARTY ||
      walletType === WalletType.PRIVATE_KEY
    ) {
      if (!walletTypeList?.includes(walletType)) {
        // 目前仅支持activityStore.activityDetail?.activityInfo.walletTypeList钱包
        const textArr = [];
        if (walletTypeList?.includes(WalletType.CLOUD)) {
          textArr.push(fm('bybitCloudWallet'));
        }
        if (walletTypeList?.includes(WalletType.MNEMONIC_PHRASE)) {
          textArr.push(fm('bybitWalletExtension'));
        }
        toastWarn(
          fm('walletTypeSwitchTip', {
            walletType: textArr.join(' , '),
          })
        );
        return;
      }
      if (item.chainId && item.chainId !== evmWalletInfo.chainId) {
        if (!activityChainInfo) {
          const taskChain = Object.values(ACTIVITY_CHAIN_INFO).find(
            (item) => item.id === chainId
          );
          if (taskChain) {
            await bybitWalletAddChain(taskChain.info);
            initThirdAddresses();
            return;
          }
        }
        // 当前连接的钱包中不存在活动链
        if (!activityChainInfo) {
          toastWarn(fm('switchToSupportedNet'));
          return;
        }
        // 非异构链才切链
        if (
          activityChainInfo &&
          activityChainInfo.chainType === WalletChainTypes.EVM
        ) {
          switchChain(item.chainId);
          return;
        }
      }
    }
    //设置当前做任务的configId
    onChangeCurrentTask(item);

    // 兼容blink结束，也要可以使用blink
    if (
      subTask.status === TaskStatus.ended &&
      subTask.taskType === SubTaskType.blink
    ) {
      const ruleConfig = safeParseJSON(subTask.taskRuleConfig, {} as any);
      doBlinkTask({
        actionId: activityId,
        blinksUrl: ruleConfig.blinksUrl,
      });
      return;
    }

    handleActivityApply(item.configId, subTask);
    return;
  };

  useEffect(() => {
    // swap活动进入页面自动领取任务
    if (
      Number(activityId) === 50 &&
      isLogin &&
      !enterPageApply &&
      activityStore.taskList?.length &&
      activityStore.taskList?.[1].activityId === Number(activityId)
    ) {
      const item = activityStore.taskList?.[1];
      handleActivityApply(item?.configId, item?.subTaskList?.[0]);
      setEnterPageApply(true);
    }
  }, [activityId, activityStore.taskList, enterPageApply, isLogin]);

  return {
    beginActivityApply,
  };
};

export const useCheckTask = (query: CommonQuery, onShowSwitchWallet) => {
  const [showVerifyResult, setShowVerifyResult] = useState(false);
  const [taskVerifyObject, setTaskVerifyObject] = useState<any>();
  const {
    isLogin,
    walletId,
    evmWalletInfo,
    walletType,
    openLoginModal,
    switchChain,
    createWallet,
    walletIsReady,
  } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const [currentTask, setCurrentTask] = useState<undefined | taskDetail>();
  const checkIsLoading = useRef(false);

  // 活动支持云钱包且未创建钱包
  const showCreateCloud = () => {
    if (
      walletType === WalletType.CLOUD &&
      !evmWalletInfo.address &&
      walletTypeList.includes(WalletType.CLOUD)
    ) {
      createWallet();
      return true;
    }
    return false;
  };

  const activityChainInfo = useStore((state) => state.activityChainInfo);

  const { toastWarn } = useToast();
  const fm = useFm();

  const activityStore = useActivityStore();

  const chainId = useMemo(
    () => activityStore.activityDetail?.activityInfo.chainId,
    [activityStore.activityDetail]
  );

  const walletTypeList = useMemo(
    () => activityStore.activityDetail?.activityInfo?.walletTypeList || [],
    [activityStore.activityDetail]
  );

  // 验证任务
  const handleTaskCheck = async (task: any, isFather = false) => {
    if (checkIsLoading.current) return;

    if (isLogin) {
      if (showCreateCloud()) return;

      if (!walletTypeList.includes(walletType)) {
        onShowSwitchWallet();
        return;
      }
      // 助记词钱包中选中链跟活动链不匹配且是非异构链时需要切链
      if (
        BybitWalletEx.includes(walletType) &&
        task?.chainId &&
        task?.chainId !== evmWalletInfo.chainId
      ) {
        // 当前连接的钱包中不存在活动链
        if (!activityChainInfo) {
          toastWarn(fm('switchToSupportedNet'));
          return;
        }
        // 非异构链才切链
        if (
          activityChainInfo &&
          activityChainInfo.chainType === WalletChainTypes.EVM
        ) {
          switchChain(task.chainId);
          return;
        }
      }
      if (
        !activityStore.activityDetail?.activityInfo.clientConfig?.pcConfig
          .support
      ) {
        toastWarn(fm('notSuppOnPc'));
        return;
      }
      checkIsLoading.current = true;
      setTaskVerifyObject(task);
      try {
        await activityStore.doTaskCheck({
          taskId: task?.configId,
          chainId,
          walletId,
          walletType,
          walletAddress: walletAddresses.find(
            (item) => +item.chainId === +chainId
          )?.walletAddress,
        });
      } catch (e) {
        checkIsLoading.current = false;
      }

      checkIsLoading.current = false;
      activityStore.fetchTaskList(query);
      if (isFather && !activityStore.taskCheckResult?.checkResult) {
        setShowVerifyResult(true);
        pageViewTrack({
          page_name: Page.AirdropArcadeDetail,
          modal_name: '验证弹窗',
          modal_result: activityStore.taskCheckResult?.checkResult
            ? '验证通过'
            : '验证不通过',
        });
      }
    } else {
      openLoginModal();
    }
  };

  const onChangeShowVerifyResult = (v: boolean) => {
    setShowVerifyResult(v);
  };

  //领取任务跳转dapp回来
  useEffect(() => {
    if (!walletIsReady) return;
    currentTask !== undefined && activityStore.fetchTaskList(query);

    const windowTabChange = () => {
      if (document.visibilityState == 'hidden') {
        //todo...
      } else if (
        document.visibilityState == 'visible' &&
        isLogin &&
        currentTask
      ) {
        handleTaskCheck(currentTask);
      }
    };

    document.addEventListener('visibilitychange', windowTabChange);

    return () => {
      document.removeEventListener('visibilitychange', windowTabChange);
    };
    // TODO 目前只加了listQuery,isLogin依赖
  }, [currentTask, query, isLogin, walletIsReady]);

  const onChangeCurrentTask = (task: taskDetail) => {
    setCurrentTask(task);
  };

  return {
    showVerifyResult,
    taskVerifyObject,
    handleTaskCheck,
    onChangeCurrentTask,
    onChangeShowVerifyResult,
  };
};

export const useLuckyDraw = (query: CommonQuery) => {
  const { walletId, walletType } = useWallet();
  const { walletAddresses } = useBybitWallet();
  const [showDrawAnimation, setShowDrawAnimation] = useState(false);
  const [showDrawResult, setShowDrawResult] = useState(false);
  const [showDelayDrawModal, setShowDelayDrawModal] = useState(false);
  let timer: any;
  const claimIsLoading = useRef(false);
  const { toastWarn } = useToast();

  const activityStore = useActivityStore();

  // Token奖励类型抽奖
  const handleDraw = async (item: taskDetail, reward: rewardInfo) => {
    if (claimIsLoading.current) return;
    claimIsLoading.current = true;
    const { success, code, message } = await activityStore.doRewardClaim({
      recordId: item?.recordId,
      rewardId: reward?.rewardId,
      chainId: activityStore.activityDetail?.activityInfo.chainId,
      walletId,
      walletType,
      walletAddress: walletAddresses.find(
        (item) =>
          +item.chainId === +activityStore.activityDetail?.activityInfo.chainId
      )?.walletAddress,
    });

    claimIsLoading.current = false;

    if (!success) {
      if (code === 33020) {
        toastWarn('systemBusyTryLater');
        return;
      }

      toastWarn(message);
      return;
    }

    if (reward.rewardForm === RewardFormType.Delay) {
      setShowDelayDrawModal(true);
      activityStore.fetchTaskList(query);
    } else {
      setShowDrawAnimation(true);
      timer = setTimeout(() => {
        setShowDrawAnimation(false);
        setShowDrawResult(true);
        clearTimeout(timer);
        activityStore.fetchTaskList(query);
      }, 3e3);
    }
  };

  const onChangeShowDrawResult = (v: boolean) => {
    setShowDrawResult(v);
  };

  const onChangeDelayDrawModal = (v: boolean) => {
    setShowDelayDrawModal(v);
  };

  return {
    showDrawResult,
    showDrawAnimation,
    showDelayDrawModal,
    handleDraw,
    onChangeShowDrawResult,
    onChangeDelayDrawModal,
  };
};

export const useInstallBW = () => {
  const [showInstallBW, setInstallBW] = useState(false);

  // 点解安装BybitWallet回来后刷新页面
  useEffect(() => {
    // showInstallBW && activityStore.fetchTaskList(listQuery);

    const windowTabChange = () => {
      if (document.visibilityState == 'hidden') {
        //todo...
      } else if (document.visibilityState == 'visible') {
        showInstallBW && window.top?.location.reload();
      }
    };

    document.addEventListener('visibilitychange', windowTabChange);

    return () => {
      document.removeEventListener('visibilitychange', windowTabChange);
    };
  }, [showInstallBW]);

  const onChangeInstallBW = (v: boolean) => {
    setInstallBW(v);
  };

  return {
    showInstallBW,
    onChangeInstallBW,
  };
};
